<script lang="ts" setup>
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useUserStore } from '../stores/user/user.store'
import { useSignOut } from '../composables/useSignOut'
import { safeRedirect } from '../utils/app-path'
import { TERMS_URL, PRIVACY_URL } from '../utils/legal-links'

// Shown to a signed-in user who has not accepted the current Terms of Use and Privacy Policy: either the terms changed or they
// never accepted (closed the wizard early). They can browse and manage their account, but not upload or post bounties until they accept.
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { terms, termsError } = storeToRefs(userStore)
const { signOut } = useSignOut()

const form = reactive({ agreesToTerms: false, agreesToPrivacyPolicy: false })
const isBusy = ref(false)

const neverAccepted = computed(() => terms.value?.acceptedVersion == null)
const heading = computed(() =>
  neverAccepted.value ? 'Accept our terms to continue' : 'We have updated our terms'
)
const canSubmit = computed(() => form.agreesToTerms && form.agreesToPrivacyPolicy && !isBusy.value)

async function onSubmit() {
  if (!canSubmit.value) return
  isBusy.value = true
  const accepted = await userStore.acceptTerms()
  isBusy.value = false
  if (!accepted) {
    // The terms may have changed under the user: make them read and tick again.
    form.agreesToTerms = false
    form.agreesToPrivacyPolicy = false
    return
  }
  await router.replace(safeRedirect(route.query.redirect) ?? { name: 'explore' })
}
</script>

<template>
  <q-page padding class="layout-constrained">
    <div class="q-pa-md q-gutter-md" data-testid="updated-terms">
      <h1 class="text-h5 q-my-none" data-testid="updated-terms-heading">{{ heading }}</h1>
      <p class="text-body1">
        <template v-if="neverAccepted">
          Before you share footage or post a bounty, please read and accept our terms.
        </template>
        <template v-else>
          We have changed our Terms of Use or Privacy Policy. Please read what changed and accept to
          keep sharing footage and posting bounties.
        </template>
        You can still browse and manage your account.
      </p>
      <p class="text-body2">
        Read:
        <a :href="TERMS_URL" target="_blank" rel="noopener noreferrer">Terms of Use</a>
        |
        <a :href="PRIVACY_URL" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
      </p>

      <q-banner
        v-if="termsError"
        rounded
        class="bg-red-1 text-red-10"
        role="alert"
        data-testid="updated-terms-error"
      >
        <template #avatar><q-icon name="mdi-alert-circle-outline" color="negative" /></template>
        {{ termsError }}
      </q-banner>

      <q-form class="q-gutter-md" @submit="onSubmit">
        <div>
          <q-checkbox v-model="form.agreesToTerms" data-testid="updated-terms-accept-terms">
            <span>
              I accept the
              <a :href="TERMS_URL" target="_blank" rel="noopener noreferrer" @click.stop
                >Terms of Use</a
              >.
            </span>
          </q-checkbox>
        </div>
        <div>
          <q-checkbox
            v-model="form.agreesToPrivacyPolicy"
            data-testid="updated-terms-accept-privacy"
          >
            <span>
              I accept the
              <a :href="PRIVACY_URL" target="_blank" rel="noopener noreferrer" @click.stop
                >Privacy Policy</a
              >.
            </span>
          </q-checkbox>
        </div>
        <div>
          <q-btn
            type="submit"
            color="primary"
            unelevated
            label="Accept and continue"
            :disable="!canSubmit"
            :loading="isBusy"
            data-testid="updated-terms-submit"
          />
        </div>
      </q-form>

      <div>
        <q-btn
          flat
          no-caps
          color="primary"
          label="Log out"
          data-testid="updated-terms-logout"
          @click="signOut"
        />
      </div>
    </div>
  </q-page>
</template>
